import { convertToStringMatchRelaxed } from 'utils';

import {
  SUPER_ADMIN_CHECK_COST_CENTER_NAME,
  SUPER_ADMIN_CHECK_COST_CENTER_NAME_SUCCESS,
  SUPER_ADMIN_CHECK_COST_CENTER_NAME_FAILURE,
} from '../types';
import type { LicenseSelectorInfo } from '../utils';

export interface CostCenterNameCheckState {
  // The name the latest check was issued for. Results for any other name are dropped by this
  // reducer, and the consumer still compares it against the currently-typed value before
  // trusting `relaxedNameMatches`/`matchingLicenseRows`/`pending`.
  name: string | null;
  relaxedNameMatches: string[];
  // One flattened { costCenter, productAccess } row per product on every hit found for `name` —
  // used by validateProductAccessNotDuplicate (license-modal/index.tsx) to check for an existing
  // product+type on this cost center name without a second backend call.
  matchingLicenseRows: LicenseSelectorInfo[];
  pending: boolean;
  error: string | null;
}

interface CostCenterNameCheckAction {
  type: string;
  name?: string;
  relaxedNameMatches?: string[];
  matchingLicenseRows?: LicenseSelectorInfo[];
  error?: string;
}

const initialState: CostCenterNameCheckState = {
  name: null,
  relaxedNameMatches: [],
  matchingLicenseRows: [],
  pending: false,
  error: null,
};

// Checks are debounced and answered asynchronously, so a result for an earlier name can arrive
// after a newer check has started. Applying it would clear `pending` for the newer name and let
// the wizard proceed on data that does not describe the typed name. The comparison is relaxed to
// match how the modal decides whether a result is current.
const isForLatestName = (state: CostCenterNameCheckState, name: string | undefined): boolean =>
  state.name !== null && convertToStringMatchRelaxed(state.name) === convertToStringMatchRelaxed(name);

const CostCenterNameCheckReducer = (
  state: CostCenterNameCheckState = initialState,
  action: CostCenterNameCheckAction,
): CostCenterNameCheckState => {
  switch (action.type) {
    case SUPER_ADMIN_CHECK_COST_CENTER_NAME:
      return {
        name: action.name ?? null,
        relaxedNameMatches: [],
        matchingLicenseRows: [],
        pending: true,
        error: null,
      };
    case SUPER_ADMIN_CHECK_COST_CENTER_NAME_SUCCESS:
      if (!isForLatestName(state, action.name)) return state;
      return {
        name: action.name ?? null,
        relaxedNameMatches: action.relaxedNameMatches ?? [],
        matchingLicenseRows: action.matchingLicenseRows ?? [],
        pending: false,
        error: null,
      };
    case SUPER_ADMIN_CHECK_COST_CENTER_NAME_FAILURE:
      if (!isForLatestName(state, action.name)) return state;
      // Fails open (not treated as a duplicate) rather than leaving the Wizard's Next/Submit
      // button stuck disabled on a transient network error — this mirrors the pre-existing
      // check's own failure mode (an unloaded legacy dataset already meant "no duplicates
      // found" with no error surfaced at all), just with the error now at least logged
      // (see the saga) instead of silently invisible.
      return {
        name: action.name ?? null,
        relaxedNameMatches: [],
        matchingLicenseRows: [],
        pending: false,
        error: action.error ?? null,
      };
    default:
      return state;
  }
};

export default CostCenterNameCheckReducer;
